import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/use-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Definir roles y permisos - Sistema de Hospedajes
export const ROLES = {
  ADMIN: 'admin',
  ADMINISTRADOR: 'administrador',
  SUPERVISOR: 'supervisor',
  COORDINADOR: 'coordinador',
  CONDUCTOR: 'conductor',
  EMPLOYEE: 'empleado',
  CLIENT: 'cliente',
};

// Módulos de la aplicación - Sistema de Hospedajes
export const MODULES = {
  // === MÓDULOS PRINCIPALES ===
  DASHBOARD: 'dashboard',
  REPORTS: 'reports',
  
  // === GESTIÓN DE PERSONAS ===
  EMPLOYEES: 'employees',
  USERS: 'users',
  
  // === INFRAESTRUCTURA ===
  EDIFICIOS: 'edificios', // Ver portafolios asignados
  EDIFICIOS_ADMIN: 'edificios_admin', // Parametrizar portafolios (CRUD)
  HOSPEDAJES: 'hospedajes', // Ver activos asignados
  HOSPEDAJES_ADMIN: 'hospedajes_admin', // Parametrizar activos (CRUD)
  WORKPLACES: 'workplaces',
  ZONAS: 'zonas',
  
  // === DOCUMENTACIÓN ===
  DOCUMENTATION: 'documentation',
  DOCUMENT_TYPES: 'document_types',
  
  // === PRODUCTOS Y TIPOS ===
  GARMENT_TYPES: 'garment_types',
  CAMAS: 'camas',
  
  // === FACTURACIÓN ===
  BILLING: 'billing',
  BILLING_GENERATE: 'billing_generate',
  BILLING_DELETE: 'billing_delete',
  
  // === PROVEEDORES Y CONSUMIBLES ===
  PROVEEDOR: 'proveedor',
  INSUMOS: 'insumos',
  
  // === LAVANDERÍA ===
  LAUNDRY: 'laundry',
  LAUNDRY_RECEPTION: 'laundry_reception',
  LAUNDRY_SUPPLIES: 'laundry_supplies',
  LAUNDRY_INVENTORY: 'laundry_inventory',
  
  // === CONDUCTOR ===
  CONDUCTOR: 'conductor', // Gestión de rutas y entregas
  CONDUCTOR_RUTA: 'conductor_ruta', // Ver y gestionar ruta de entregas (solo conductor)
  RUTA_ADMIN: 'ruta_admin', // Gestión de rutas para admin y conductor
  EXTRA_RUTAS_ADMIN: 'extra_rutas_admin', // Gestión de extra rutas (solo admin)
  
  // === SOLICITUDES ===
  SOLICITUDES: 'solicitudes', // Gestión de solicitudes y bugs
  
  // === PQRs ===
  PQRS: 'pqrs', // Peticiones, Quejas y Reclamos

  // === ASISTENCIA ===
  ATTENDANCE: 'attendance', // Marcación de entrada/salida del personal
};

// Categorías de módulos para mejor organización
export const MODULE_CATEGORIES = {
  CORE: {
    name: 'Principal',
    modules: [MODULES.DASHBOARD, MODULES.REPORTS],
    icon: 'LayoutDashboard'
  },
  PEOPLE: {
    name: 'Gestión de Personas',
    modules: [MODULES.EMPLOYEES, MODULES.USERS],
    icon: 'Users'
  },
  INFRASTRUCTURE: {
    name: 'Infraestructura',
    modules: [MODULES.EDIFICIOS, MODULES.EDIFICIOS_ADMIN, MODULES.HOSPEDAJES, MODULES.HOSPEDAJES_ADMIN, MODULES.WORKPLACES, MODULES.ZONAS],
    icon: 'Building'
  },
  DOCUMENTATION: {
    name: 'Documentación',
    modules: [MODULES.DOCUMENTATION, MODULES.DOCUMENT_TYPES],
    icon: 'FileText'
  },
  PRODUCTS: {
    name: 'Productos y Tipos',
    modules: [MODULES.GARMENT_TYPES],
    icon: 'Shirt'
  },
  BILLING: {
    name: 'Facturación',
    modules: [MODULES.BILLING, MODULES.BILLING_GENERATE, MODULES.BILLING_DELETE],
    icon: 'DollarSign'
  },
  PROVEEDOR: {
    name: 'Proveedor',
    modules: [MODULES.PROVEEDOR],
    icon: 'Users'
  },
  INSUMOS: {
    name: 'Insumos',
    modules: [MODULES.INSUMOS],
    icon: 'Package'
  },
  LAUNDRY: {
    name: 'Lavandería',
    modules: [MODULES.LAUNDRY, MODULES.LAUNDRY_RECEPTION, MODULES.LAUNDRY_SUPPLIES, MODULES.LAUNDRY_INVENTORY],
    icon: 'Shirt'
  }
};

// Permisos por rol - Sistema de Hospedajes (organizados por categorías)
const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: {
    // Acceso completo a todos los módulos
    core: [MODULES.DASHBOARD, MODULES.REPORTS],
    people: [MODULES.EMPLOYEES, MODULES.USERS, MODULES.ATTENDANCE],
    infrastructure: [MODULES.EDIFICIOS, MODULES.EDIFICIOS_ADMIN, MODULES.HOSPEDAJES, MODULES.HOSPEDAJES_ADMIN, MODULES.WORKPLACES, MODULES.ZONAS],
    documentation: [MODULES.DOCUMENTATION, MODULES.DOCUMENT_TYPES],
    products: [MODULES.GARMENT_TYPES, MODULES.CAMAS],
    billing: [MODULES.BILLING, MODULES.BILLING_GENERATE, MODULES.BILLING_DELETE],
    proveedor: [MODULES.PROVEEDOR],
    consumables: [MODULES.INSUMOS],
    laundry: [MODULES.LAUNDRY, MODULES.LAUNDRY_RECEPTION, MODULES.LAUNDRY_SUPPLIES, MODULES.LAUNDRY_INVENTORY],
    conductor: [MODULES.RUTA_ADMIN, MODULES.EXTRA_RUTAS_ADMIN],
    solicitudes: [MODULES.SOLICITUDES],
    pqrs: [MODULES.PQRS],
  },
  [ROLES.ADMINISTRADOR]: {
    // Acceso completo a todos los módulos (igual que ADMIN)
    core: [MODULES.DASHBOARD, MODULES.REPORTS],
    people: [MODULES.EMPLOYEES, MODULES.USERS, MODULES.ATTENDANCE],
    infrastructure: [MODULES.EDIFICIOS, MODULES.EDIFICIOS_ADMIN, MODULES.HOSPEDAJES, MODULES.HOSPEDAJES_ADMIN, MODULES.WORKPLACES, MODULES.ZONAS],
    documentation: [MODULES.DOCUMENTATION, MODULES.DOCUMENT_TYPES],
    products: [MODULES.GARMENT_TYPES, MODULES.CAMAS],
    billing: [MODULES.BILLING, MODULES.BILLING_GENERATE, MODULES.BILLING_DELETE],
    proveedor: [MODULES.PROVEEDOR],
    consumables: [MODULES.INSUMOS],
    laundry: [MODULES.LAUNDRY, MODULES.LAUNDRY_RECEPTION, MODULES.LAUNDRY_SUPPLIES, MODULES.LAUNDRY_INVENTORY],
    conductor: [MODULES.RUTA_ADMIN, MODULES.EXTRA_RUTAS_ADMIN],
    solicitudes: [MODULES.SOLICITUDES],
    pqrs: [MODULES.PQRS],
  },
  [ROLES.SUPERVISOR]: {
    // Acceso limitado para supervisión
    core: [MODULES.DASHBOARD, MODULES.REPORTS],
    people: [MODULES.EMPLOYEES, MODULES.ATTENDANCE], // Sin gestión de usuarios
    infrastructure: [MODULES.EDIFICIOS, MODULES.EDIFICIOS_ADMIN, MODULES.HOSPEDAJES, MODULES.HOSPEDAJES_ADMIN, MODULES.WORKPLACES, MODULES.ZONAS],
    documentation: [MODULES.DOCUMENTATION], // Sin tipos de documento
    products: [MODULES.GARMENT_TYPES],
    billing: [], // Sin acceso a facturación
    pqrs: [MODULES.PQRS],
    solicitudes: [MODULES.SOLICITUDES],
    attendance: [MODULES.ATTENDANCE],


  },
  [ROLES.COORDINADOR]: {
    // Acceso limitado: Dashboard, Portafolios, Solicitudes del Día e Inventario Vertical
    core: [MODULES.DASHBOARD],
    people: [], // Sin gestión de personas
    infrastructure: [MODULES.EDIFICIOS], // Solo VER portafolios asignados y Solicitudes del Día
    documentation: [], // Sin acceso a documentación
    products: [], // Sin acceso a tipos de prenda
    billing: [], // Sin acceso a facturación
    proveedor: [],
    consumables: [],
    laundry: [MODULES.LAUNDRY_INVENTORY], // Acceso a inventario vertical
    conductor: [],
    pqrs: [MODULES.PQRS],
    solicitudes: [MODULES.SOLICITUDES],
    attendance: [MODULES.ATTENDANCE],

  },
  [ROLES.CONDUCTOR]: {
    // Acceso SOLO a ruta y entregas (sin dashboard)
    core: [],
    people: [],
    infrastructure: [],
    documentation: [],
    products: [],
    billing: [],
    proveedor: [],
    consumables: [],
    laundry: [],
    conductor: [MODULES.RUTA_ADMIN, MODULES.CONDUCTOR_RUTA],
    pqrs: [MODULES.PQRS],
    attendance: [MODULES.ATTENDANCE],
  },
  [ROLES.EMPLOYEE]: {
    // Acceso mínimo
    core: [MODULES.DASHBOARD],
    people: [],
    infrastructure: [],
    documentation: [],
    products: [],
    billing: [],
    conductor: [],
    pqrs: [MODULES.PQRS],
    attendance: [MODULES.ATTENDANCE],
  },
  [ROLES.CLIENT]: {
    // Solo dashboard
    core: [MODULES.DASHBOARD],
    people: [],
    infrastructure: [],
    documentation: [],
    products: [],
    billing: [],
    conductor: [],
    pqrs: [MODULES.PQRS],
  },
};

// Función helper para obtener todos los módulos de un rol
const getAllModulesForRole = (role) => {
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return [];
  
  return [
    ...permissions.core,
    ...permissions.people,
    ...permissions.infrastructure,
    ...permissions.documentation,
    ...permissions.products,
    ...permissions.billing,
    ...permissions.proveedor,
    ...permissions.consumables,
    ...(permissions.conductor || []),
    ...permissions.laundry,
    ...(permissions.solicitudes || []),
    ...(permissions.pqrs || []),
  ];
};

// Nota: Admin ya incluye USERS arriba

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Cargar usuario desde localStorage al iniciar
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing user data', error);
        localStorage.removeItem('user');
      }
    }
    if (!storedToken) {
      localStorage.removeItem('token');
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const resp = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correo: email, contrasena: password }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        const message = err.message || 'Credenciales inválidas';
        toast({ title: 'Error de autenticación', description: message, variant: 'destructive' });
        return false;
      }

      const data = await resp.json();
      const token = data.token || data.accessToken;
      const backendUser = data.user || data.usuario || data.data || {};

      const normalizedUser = {
        id: backendUser._id || backendUser.id,
        email: backendUser.correo || backendUser.email,
        name: backendUser.nombre || backendUser.name,
        role: backendUser.rol || backendUser.role,
        lavanderia: backendUser.lavanderia || null,
        estado: backendUser.estado,
        edificiosAsignados: backendUser.edificiosAsignados || [],
        raw: backendUser,
      };

      if (!normalizedUser.role) {
        toast({ title: 'Error de autenticación', description: 'Usuario sin rol asignado', variant: 'destructive' });
        return false;
      }

      if (token) localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);

      toast({ title: 'Inicio de sesión exitoso', description: `Bienvenido ${normalizedUser.name}` });
      return normalizedUser;
    } catch (error) {
      console.error('Login error:', error);
      toast({ title: 'Error de autenticación', description: 'No fue posible iniciar sesión. Intenta nuevamente.', variant: 'destructive' });
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const forgotPassword = async (email) => {
    try {
      const resp = await fetch(`${API_URL}/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ correo: email }) });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const message = data.message || 'No fue posible procesar la solicitud';
        toast({ title: 'Recuperación de contraseña', description: message, variant: 'destructive' });
        return { ok: false };
      }
      const tokenDev = data.token;
      toast({ title: 'Recuperación de contraseña', description: 'Si el correo existe, se enviaron instrucciones.' });
      return { ok: true, token: tokenDev };
    } catch (e) {
      toast({ title: 'Recuperación de contraseña', description: 'Error de red', variant: 'destructive' });
      return { ok: false };
    }
  };

  const resetPassword = async ({ email, token, nueva }) => {
    try {
      const resp = await fetch(`${API_URL}/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ correo: email, token, nueva }) });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const message = data.message || 'Token inválido o expirado';
        toast({ title: 'Restablecer contraseña', description: message, variant: 'destructive' });
        return false;
      }
      toast({ title: 'Restablecer contraseña', description: 'Contraseña actualizada' });
      return true;
    } catch (e) {
      toast({ title: 'Restablecer contraseña', description: 'Error de red', variant: 'destructive' });
      return false;
    }
  };

  const changePassword = async ({ actual, nueva }) => {
    try {
      const email = user?.email;
      if (!email) {
        toast({ title: 'Cambiar contraseña', description: 'Sesión inválida', variant: 'destructive' });
        return false;
      }
      const resp = await fetch(`${API_URL}/auth/change-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ correo: email, actual, nueva }) });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const message = data.message || 'No fue posible actualizar la contraseña';
        toast({ title: 'cambiar contraseña', description: message, variant: 'destructive' });
        return false;
      }
      toast({ title: 'cambiar contraseña', description: 'Contraseña actualizada correctamente' });
      return true;
    } catch (e) {
      toast({ title: 'cambiar contraseña', description: 'Error de red', variant: 'destructive' });
      return false;
    }
  };

  const hasPermission = (module) => {
    if (!user) return false;
    
    // Los administradores siempre tienen acceso a todos los módulos definidos en ROLE_PERMISSIONS
    if (user.role === ROLES.ADMIN || user.role === ROLES.ADMINISTRADOR) {
      const adminModules = getAllModulesForRole(user.role === ROLES.ADMIN ? ROLES.ADMIN : ROLES.ADMINISTRADOR);
      return adminModules.includes(module);
    }
    
    // Para otros roles, priorizar módulos personalizados del usuario si existen
    const custom = user?.raw?.modulosActivos;
    if (Array.isArray(custom) && custom.length > 0) {
      return custom.includes(module);
    }
    
    // Usar permisos por rol con la nueva estructura
    const roleModules = getAllModulesForRole(user.role);
    return roleModules.includes(module);
  };
  
  // Función para verificar permisos por categoría
  const hasPermissionInCategory = (category) => {
    if (!user) return false;
    
    const permissions = ROLE_PERMISSIONS[user.role];
    if (!permissions) return false;
    
    return permissions[category] && permissions[category].length > 0;
  };
  
  // Verificar si el usuario es administrador
  const isAdmin = () => {
    if (!user) return false;
    return user.role === ROLES.ADMIN || user.role === ROLES.ADMINISTRADOR;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    hasPermission,
    hasPermissionInCategory,
    isAdmin,
    ROLES,
    MODULES,
    MODULE_CATEGORIES,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// Componente de protección de rutas
export const ProtectedRoute = ({ children, requiredModule }) => {
  const { user, loading, hasPermission } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (!loading && requiredModule && !hasPermission(requiredModule)) {
      navigate('/unauthorized');
    }
  }, [user, loading, hasPermission, requiredModule, navigate]);

  if (loading || !user || (requiredModule && !hasPermission(requiredModule))) {
    return <div>Cargando...</div>; // Todo: Implementar un componente de carga
  }

  return children;
};
